import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Sparkles, GraduationCap, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TEMPLATES = [
  { id: "classic", name: "Classic Classroom", desc: "Balanced layout with agenda front and center.", emojiless: "grid" },
  { id: "planner", name: "Planner First", desc: "For teachers who live in their lesson plans.", emojiless: "book" },
  { id: "minimal", name: "Calm & Minimal", desc: "Fewer widgets, more breathing room.", emojiless: "leaf" },
];

const THEMES = [
  { id: "periwinkle", name: "Periwinkle", color: "#8FA9E3" },
  { id: "mint", name: "Fresh Mint", color: "#6FC2B0" },
  { id: "peach", name: "Soft Peach", color: "#EE9C86" },
  { id: "lavender", name: "Lavender", color: "#B69CE0" },
  { id: "sky", name: "Sky Blue", color: "#78B7E0" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading, applyTheme, refreshUser } = useAuth();
  const [template, setTemplate] = useState("classic");
  const [theme, setTheme] = useState("periwinkle");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    if (!loading && user?.onboarded) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/complete", { template, theme });
      applyTheme(theme);
      await refreshUser();
      toast.success("Your command center is ready!");
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-background tcc-dots py-12 px-5">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-2xl bg-primary flex items-center justify-center">
            <GraduationCap className="text-primary-foreground" strokeWidth={1.6} />
          </div>
          <span className="font-heading font-extrabold text-slate-800">Set up your space</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-800">Hi {user.name.split(" ")[0]} 👋</h1>
          <p className="text-slate-500 mt-2 mb-8">Pick a starter template and a theme. You can change everything later.</p>

          <h2 className="font-heading text-lg font-bold text-slate-700 mb-3">Choose a starter template</h2>
          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                data-testid={`template-${t.id}`}
                onClick={() => setTemplate(t.id)}
                className={`text-left p-5 rounded-2xl border-2 transition-all duration-200 ${
                  template === t.id ? "border-primary bg-primary/8" : "border-slate-100 bg-white hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading font-bold text-slate-800">{t.name}</span>
                  {template === t.id && <Check size={18} className="text-primary" />}
                </div>
                <p className="text-sm text-slate-500">{t.desc}</p>
              </button>
            ))}
          </div>

          <h2 className="font-heading text-lg font-bold text-slate-700 mb-3">Pick your color theme</h2>
          <div className="flex flex-wrap gap-3 mb-10">
            {THEMES.map((th) => (
              <button
                key={th.id}
                data-testid={`theme-${th.id}`}
                onClick={() => { setTheme(th.id); applyTheme(th.id); }}
                className={`flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full border-2 transition-all ${
                  theme === th.id ? "border-slate-800 bg-white" : "border-slate-100 bg-white hover:border-slate-300"
                }`}
              >
                <span className="h-7 w-7 rounded-full" style={{ background: th.color }} />
                <span className="text-sm font-medium text-slate-700">{th.name}</span>
              </button>
            ))}
          </div>

          <Button onClick={finish} disabled={saving} data-testid="onboarding-finish-button"
            className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base">
            {saving ? <Loader2 className="animate-spin" /> : <><Sparkles size={18} className="mr-1" /> Enter my command center</>}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
